
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { CurveState } from "../types/bonding-curve";
import { BONDING_CURVE_TARGET_SOL, BONDING_CURVE_TARGET_SUI, VIRTUAL_SOL_RESERVES, VIRTUAL_SUI_RESERVES, INITIAL_TOKEN_SUPPLY } from "./constants";

/**
 * In-Memory Store for Bonding Curve States.
 * In a real application, this would be the On-Chain Program Account state.
 */
class BondingCurveDatabase {
    private curves: Record<string, CurveState> = {};

    constructor() {
        // Initialize with some mock data for existing tokens in MARKET_DATA
        this.initializeMockCurve('HotDAO11111111111111111111111111111111111', 'SOL', 45); // 50% progress
        this.initializeMockCurve('0xSuiPepeAddress...', 'SUI', 2500); // 50% progress
    }

    getCurve(mintAddress: string): CurveState | null {
        return this.curves[mintAddress] || null;
    }

    getAllCurves(): CurveState[] {
        return Object.values(this.curves);
    }

    /**
     * Called when a user Mints a new token via TokenForm
     */
    initializeCurve(mintAddress: string, chain: 'SOL' | 'SUI'): CurveState {
        const target = chain === 'SOL' ? BONDING_CURVE_TARGET_SOL : BONDING_CURVE_TARGET_SUI;
        const virtualReserves = chain === 'SOL' ? VIRTUAL_SOL_RESERVES : VIRTUAL_SUI_RESERVES;
        
        // Initial Price = Virtual Sol / Virtual Tokens
        const initialPrice = virtualReserves / INITIAL_TOKEN_SUPPLY;

        const newState: CurveState = {
            mintAddress,
            chain,
            virtualSolReserves: virtualReserves,
            virtualTokenReserves: INITIAL_TOKEN_SUPPLY,
            realSolReserves: 0,
            realTokenReserves: 0,
            tokenPrice: initialPrice,
            marketCap: initialPrice * INITIAL_TOKEN_SUPPLY,
            progress: 0,
            isGraduated: false,
            // Fair Launch / Bot Protection Defaults
            security: {
                maxTradeAmount: INITIAL_TOKEN_SUPPLY * 0.05, // Max 5% of supply buy limit initially
                launchTimestamp: Date.now(),
                antiSnipeEnabled: true
            }
        };

        this.curves[mintAddress] = newState;
        this.notifyListeners();
        return newState;
    }

    /**
     * Updates the curve state after a trade
     */
    updateCurve(mintAddress: string, newState: Partial<CurveState>) {
        if (!this.curves[mintAddress]) return;
        this.curves[mintAddress] = { ...this.curves[mintAddress], ...newState };
        this.notifyListeners();
    }

    private initializeMockCurve(mint: string, chain: 'SOL'|'SUI', collected: number) {
        const target = chain === 'SOL' ? BONDING_CURVE_TARGET_SOL : BONDING_CURVE_TARGET_SUI;
        const virtual = chain === 'SOL' ? VIRTUAL_SOL_RESERVES : VIRTUAL_SUI_RESERVES;
        
        // Simulate a curve that has already collected some funds
        // k = x * y
        const k = virtual * INITIAL_TOKEN_SUPPLY;
        const newVirtualSol = virtual + collected;
        const newVirtualTokens = k / newVirtualSol;
        const price = newVirtualSol / newVirtualTokens;

        this.curves[mint] = {
            mintAddress: mint,
            chain,
            virtualSolReserves: newVirtualSol,
            virtualTokenReserves: newVirtualTokens,
            realSolReserves: collected,
            realTokenReserves: INITIAL_TOKEN_SUPPLY - newVirtualTokens,
            tokenPrice: price,
            marketCap: price * INITIAL_TOKEN_SUPPLY,
            progress: (collected / target) * 100,
            isGraduated: false,
            security: {
                maxTradeAmount: INITIAL_TOKEN_SUPPLY, // No limit for old mocks
                launchTimestamp: Date.now() - 10000000,
                antiSnipeEnabled: false
            }
        };
    }

    // Simple Event Emitter for React Hook
    private listeners: Function[] = [];
    subscribe(listener: Function) {
        this.listeners.push(listener);
        return () => {
            this.listeners = this.listeners.filter(l => l !== listener);
        };
    }
    private notifyListeners() {
        this.listeners.forEach(l => l());
    }
}

export const bondingCurveDB = new BondingCurveDatabase();
